import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  Upload, Plus, Search, AlertTriangle, Shield, Server,
  Trash2, Loader2, FileUp
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const VulnScanner = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [scanners, setScanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [selectedScanner, setSelectedScanner] = useState("manual");
  const [findings, setFindings] = useState("");
  const [importing, setImporting] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualFinding, setManualFinding] = useState({
    title: "", severity: "medium", description: "", affected_assets: ""
  });

  const fetchScanners = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vuln-scanner/supported`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScanners(res.data);
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchScanners(); }, [fetchScanners]);

  const processFindings = async () => {
    setImporting(true);
    try {
      let parsed;
      try {
        parsed = JSON.parse(findings);
        if (!Array.isArray(parsed)) parsed = [parsed];
      } catch {
        toast.error("Invalid JSON. Please paste valid JSON array.");
        setImporting(false);
        return;
      }
      const res = await axios.post(`${API}/vuln-scanner/process?scanner=${selectedScanner}`, parsed, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Imported ${res.data.imported} finding(s) to Threat Intel`);
      setShowImport(false);
      setFindings("");
    } catch (error) {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const addManualFinding = async () => {
    if (!manualFinding.title) { toast.error("Title required"); return; }
    setImporting(true);
    try {
      const finding = {
        title: manualFinding.title,
        severity: manualFinding.severity,
        description: manualFinding.description,
        affected_assets: manualFinding.affected_assets ? manualFinding.affected_assets.split(",").map(s => s.trim()) : []
      };
      const res = await axios.post(`${API}/vuln-scanner/process?scanner=manual`, [finding], {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Finding imported to Threat Intel`);
      setShowManual(false);
      setManualFinding({ title: "", severity: "medium", description: "", affected_assets: "" });
    } catch (error) {
      toast.error("Failed to add finding");
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="vuln-scanner-page">
      <PageHeader
        title={t("vuln.title")}
        description={t("vuln.subtitle")}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowManual(true)} data-testid="manual-finding-btn">
              <Plus className="w-4 h-4 mr-2" /> Manual Entry
            </Button>
            <Button onClick={() => setShowImport(true)} data-testid="import-scan-btn">
              <Upload className="w-4 h-4 mr-2" /> Import Scan
            </Button>
          </div>
        }
      />

      {/* Supported Scanners */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scanners.map(scanner => (
          <motion.div key={scanner.id} whileHover={{ scale: 1.02 }}>
            <Card className="border-border hover:border-primary/50 transition-colors cursor-pointer h-full"
              onClick={() => { setSelectedScanner(scanner.id); setShowImport(true); }}
              data-testid={`scanner-${scanner.id}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    {scanner.id === "manual" ? <Plus className="w-5 h-5" /> : <Shield className="w-5 h-5 text-primary" />}
                  </div>
                  <div>
                    <h3 className="font-medium text-sm">{scanner.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{scanner.description}</p>
                    <Badge variant="secondary" className="mt-2 text-xs">{scanner.format}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* How it works */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-lg">How to Import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>1. Export scan results from your vulnerability scanner in JSON format</p>
          <p>2. Select the scanner type and paste the JSON data</p>
          <p>3. Findings are automatically imported into the Threat Intelligence module</p>
          <p>4. Each finding creates a threat intel entry with severity, IOCs, and affected assets</p>
          <div className="mt-4 p-3 rounded-lg bg-muted/30 font-mono text-xs">
            <p className="text-muted-foreground mb-1">Expected JSON format:</p>
            <pre>{`[
  {
    "title": "CVE-2024-1234 - SQL Injection",
    "severity": "critical",
    "description": "SQL injection vulnerability...",
    "affected_assets": ["web-server-01"],
    "iocs": ["192.168.1.100"],
    "mitre_techniques": ["TA0001"]
  }
]`}</pre>
          </div>
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Scan Results</DialogTitle>
            <DialogDescription>Paste vulnerability scan results in JSON format</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Scanner</Label>
              <Select value={selectedScanner} onValueChange={setSelectedScanner}>
                <SelectTrigger data-testid="import-scanner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {scanners.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Findings (JSON)</Label>
              <Textarea value={findings} onChange={(e) => setFindings(e.target.value)}
                placeholder='[{"title": "...", "severity": "high", "description": "..."}]'
                rows={10} className="font-mono text-xs"
                data-testid="import-findings-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImport(false)}>Cancel</Button>
            <Button onClick={processFindings} disabled={importing || !findings.trim()} data-testid="process-import-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Import Findings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={showManual} onOpenChange={setShowManual}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manual Vulnerability Entry</DialogTitle>
            <DialogDescription>Add a finding manually</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={manualFinding.title} onChange={(e) => setManualFinding({ ...manualFinding, title: e.target.value })}
                placeholder="e.g., CVE-2024-1234 - SQL Injection" data-testid="manual-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={manualFinding.severity} onValueChange={(v) => setManualFinding({ ...manualFinding, severity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={manualFinding.description} onChange={(e) => setManualFinding({ ...manualFinding, description: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Affected Assets (comma-separated)</Label>
              <Input value={manualFinding.affected_assets} onChange={(e) => setManualFinding({ ...manualFinding, affected_assets: e.target.value })}
                placeholder="server-01, db-prod" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManual(false)}>Cancel</Button>
            <Button onClick={addManualFinding} disabled={importing} data-testid="save-manual-finding-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add Finding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VulnScanner;
